import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import { analyticsApi } from '../api/analytics.api';
import type { TimePeriod, AnalyticsFilters, AnalyticsResponse } from '../types';  // ← добавлен AnalyticsResponse
import { REFRESH_INTERVAL } from '../constants';

const QUERY_KEY = ['analytics'] as const;

// Явная типизация возвращаемого значения useQuery
export const useAnalytics = (filters: AnalyticsFilters) => {
    const queryClient = useQueryClient();

    const {
        data,
        isLoading,
        error,
        dataUpdatedAt,
    } = useQuery<AnalyticsResponse>({
        queryKey: [...QUERY_KEY, filters.period, filters.selectedSites],
        queryFn: ({ signal }) => 
            analyticsApi.getAnalytics(filters.period, filters.selectedSites, signal),
        staleTime: 10000,
        refetchInterval: REFRESH_INTERVAL,
        refetchOnWindowFocus: true,
    });

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    }, [queryClient]);

    useEffect(() => {
        const periods: TimePeriod[] = ['24h', '7d', '30d'];
        periods.forEach(period => {
            if (period !== filters.period) {
                queryClient.prefetchQuery({
                    queryKey: [...QUERY_KEY, period, filters.selectedSites],
                    queryFn: ({ signal }) => 
                        analyticsApi.getAnalytics(period, filters.selectedSites, signal),
                    staleTime: 5 * 60 * 1000,
                });
            }
        });
    }, [filters.period, filters.selectedSites, queryClient]);

    const lastUpdateText = dataUpdatedAt 
        ? new Date(dataUpdatedAt).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
        })
        : null;

    return {
        summary: data?.summary,
        sites: data?.sites ?? [],
        timeSeries: data?.timeSeries ?? [],
        heatmaps: data?.heatmaps ?? [],  // ← теперь работает
        isLoading,
        error,
        refresh,
        lastUpdateText,
        isStale: Date.now() - (dataUpdatedAt ?? 0) > REFRESH_INTERVAL * 2,
    };
};